import type { Token } from '@/store'

import axios, { type AxiosInstance, type CreateAxiosDefaults } from 'axios'
import { store } from '@/storehandler/tokens'
import { getAppInfo } from '@/native/appinfo'

export default class TTAxios {
  static create(config?: CreateAxiosDefaults): AxiosInstance {
    const instance = axios.create({
      withCredentials: true,
      ...config
    })

    // Setup default response interceptor
    instance.interceptors.response.use(
      async (response) => {
        const token = response.headers['tt-token']
        const refreshToken = response.headers['tt-refresh-token']
        if (token) {
          let [tok, exp] = token.split(':')
          // localstoragestore
          store.set('tt-token', {
            token: tok,
            expires: new Date(exp * 1000)
          } as Token)
        }
        // Note: Refresh token is currently not used
        if (refreshToken) {
          let [tok, exp] = token.split(':')
          store.set('tt-refresh-token', {
            token: tok,
            expires: new Date(exp * 1000)
          } as Token)
        }
        return response
      },
      (error) => {
        // Cancelled requests (AbortController) aren't real errors — pass them
        // through as-is so callers' own cancellation checks still work.
        if (axios.isCancel(error)) {
          return Promise.reject(error)
        }

        let err = {} as Error
        if (axios.isAxiosError(error)) {
          if (error.response?.status === 401) {
            // redirect to login page
            window.location.href = '/login'
          } else if (error.response?.status === 426) {
            // app build is below the backend's minimum supported version
            window.location.href = '/update-required'
          } else {
            err = new Error(error.response?.data?.error || 'error.unknown')
          }
        } else if (error instanceof Error) {
          err = error
        }

        return Promise.reject(err)
      }
    )

    // Setup default request interceptor
    instance.interceptors.request.use(
      (config) => {
        const token = store.get('tt-token')
        const refreshToken = store.get('tt-refresh-token')
        const now = new Date()
        if (token && token?.token && token.expires > now) {
          config.headers['tt-token'] = token.token
        }
        if (refreshToken && refreshToken?.token && refreshToken.expires > now) {
          config.headers['tt-refresh-token'] = refreshToken.token
        }
        const appInfo = getAppInfo()
        if (appInfo) {
          config.headers['X-App-Version'] = appInfo.version
          config.headers['X-App-Platform'] = appInfo.platform
        }
        return config
      },
      (error) => {
        let err = {} as Error
        if (axios.isAxiosError(error)) {
          err = new Error(error.response?.data?.error || 'error.unknown')
        } else if (error instanceof Error) {
          err = error
        }

        return Promise.reject(err)
      }
    )

    return instance
  }

  static isAxiosError = axios.isAxiosError
  static isCancel = axios.isCancel
}
